import { useRef, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { apiErrorMessage } from "@/components/EmailTemplateRecipients";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Loader2, MapPin, Trash2, Upload } from "lucide-react";

// A function, not a constant: `API` is not yet initialised while this file loads
// (App.js imports it — circular import).
const base = () => `${API}/notification-config/admin/client-announcement`;

/**
 * Optional affected-locations table, from an uploaded .xlsx/.csv only (no manual
 * entry). The backend parses and validates it (POST …/locations/parse); the rows are
 * shown here for review and go into the email as a table. A file with problems is
 * shown with its row errors and blocks Trigger Now until it is fixed or removed.
 */
export default function LocationsUpload({ type, columns, limits, upload, setUpload, disabled }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(null); // "template" | "upload"

  const downloadTemplate = async () => {
    setBusy("template");
    try {
      const res = await axios.get(`${base()}/locations-template`,
        { params: { announcement_type: type }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${type}_template.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not download the template"));
    } finally {
      setBusy(null);
    }
  };

  const onFile = async (file) => {
    if (!file) return;
    const ext = file.name.includes(".") ? file.name.split(".").pop().toLowerCase() : "";
    if (!limits.upload_extensions.includes(ext)) {
      toast.error(ext === "xls" ? "Save the .xls file as .xlsx and upload again" : "Upload a .xlsx or .csv file");
      return;
    }
    if (file.size > limits.max_upload_bytes) {
      toast.error(`${file.name} is larger than ${limits.max_upload_bytes / (1024 * 1024)} MB`);
      return;
    }
    setBusy("upload");
    const form = new FormData();
    form.append("announcement_type", type);
    form.append("file", file, file.name);
    try {
      const { data } = await axios.post(`${base()}/locations/parse`, form);
      setUpload(data);
      if (data.errors.length) toast.error(`${data.errors.length} problem(s) in ${data.file_name} — see the list below`);
      else toast.success(`Loaded ${data.rows.length} location(s) from ${data.file_name}`);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not read the file"));
    } finally {
      setBusy(null);
    }
  };

  const shownColumns = upload ? columns.filter((c) => upload.rows.some((r) => r[c.key])) : [];
  const required = columns.filter((c) => c.required).map((c) => c.label).join(", ");
  const optional = columns.filter((c) => !c.required).map((c) => c.label).join(", ");

  return (
    <Card data-testid="ca-locations">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2"><MapPin className="w-5 h-5 text-gray-500" />Affected locations (optional)</CardTitle>
        <p className="text-xs text-gray-500">
          Upload a .xlsx or .csv file — its rows appear as a table in the email; up to {limits.max_locations} rows.
          Leave this empty if not needed. Download the sample template for this announcement type's columns.
        </p>
        <p className="text-xs text-gray-500" data-testid="ca-locations-columns">
          <span className="font-medium text-gray-700">Mandatory:</span> {required}
          {optional && <>{" · "}<span className="font-medium text-gray-700">Optional:</span> {optional}</>}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <input ref={inputRef} type="file" className="hidden" accept=".xlsx,.csv"
          onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ""; }} data-testid="ca-locations-input" />
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" disabled={disabled || Boolean(busy)}
            onClick={() => inputRef.current?.click()} data-testid="ca-locations-upload">
            {busy === "upload" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
            {upload ? "Replace file" : "Upload Excel / CSV"}
          </Button>
          <Button type="button" variant="ghost" disabled={Boolean(busy)} onClick={downloadTemplate}
            data-testid="ca-locations-template">
            {busy === "template" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
            Download sample template
          </Button>
        </div>

        {upload && (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="flex items-center gap-1.5 text-sm text-gray-800 min-w-0">
                <FileSpreadsheet className="w-4 h-4 text-teal-600 shrink-0" />
                <span className="break-all">{upload.file_name}</span>
                <span className="text-gray-500 whitespace-nowrap">· {upload.rows.length} row(s)</span>
              </p>
              <Button type="button" variant="ghost" size="sm" className="text-red-600 hover:text-red-700"
                disabled={disabled} onClick={() => setUpload(null)} data-testid="ca-locations-remove">
                <Trash2 className="w-4 h-4 mr-1" />Remove
              </Button>
            </div>
            {upload.warnings.map((w) => <p key={w} className="text-xs text-amber-700">{w}</p>)}
            {upload.errors.length > 0 && (
              <ul className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 space-y-0.5 max-h-40 overflow-y-auto"
                role="alert" data-testid="ca-locations-errors">
                {upload.errors.map((e, i) => <li key={i}>Sheet line {e.line}: {e.message}</li>)}
              </ul>
            )}
            {/* Review table — scrolls sideways inside the card on narrow screens */}
            <div className="overflow-x-auto max-h-72 overflow-y-auto rounded-md border border-gray-200">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Line</th>
                    {shownColumns.map((c) => (
                      <th key={c.key} className="px-3 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {upload.rows.map((row, i) => {
                    const bad = upload.errors.some((e) => e.row === i);
                    return (
                      <tr key={i} className={bad ? "bg-red-50/60" : ""}>
                        <td className="px-3 py-2 text-xs text-gray-500 tabular-nums">{upload.source_lines[i]}</td>
                        {shownColumns.map((c) => <td key={c.key} className="px-3 py-2 text-gray-800">{row[c.key] || "—"}</td>)}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
